  <template>
  <section v-if="screenContent" class="scene scene--underground">
    <!-- background -->
    <div class="underlay">
      <div class="underlay wall" />
      <div class="underlay column column--left" />
      <div class="underlay column column--right" />
      <div class="underlay bg-gradient-wall-up" />
      <div class="underlay road" />
      <img alt="Platform Left" src="@/assets/images/underground/platformleft.png" class="platform platform--left">
      <img alt="Platform Right" src="@/assets/images/underground/platformright.png" class="platform platform--right">
    </div>
    <!-- people -->
    <UndergroundNpc1 v-if="screenContent.NPC1" :npcs="screenContent.NPC1" :speechbubble="screenContent.SpeechBubble" />
    <UndergroundNpc2 v-if="screenContent.NPC2" :npcs="screenContent.NPC2" />
    <UndergroundNpc3 v-if="screenContent.NPC3" :npcs="screenContent.NPC3" />
    <UndergroundNpc4 v-if="screenContent.NPC4" :npcs="screenContent.NPC4" />
    <!-- content -->
    <div class="content" :class="{ 'typeset' : activeType }">
      <div class="container text-center">
        <h2 v-if="screenContent.Heading" class="mb-1">
          {{ screenContent.Heading }}
        </h2>
        <h4 v-if="screenContent.SubHeading" class="mb-2">
          {{ screenContent.SubHeading }}
        </h4>
        <p v-if="screenContent.Description" class="portal__text">
          {{ screenContent.Description }}
        </p>
        <div class="portal__btns flex-row">
          <button
            v-for="(type, index) in screenContent.Classes"
            :key="`btn-type-${index}`"
            class="btn btn--pink btn--half"
            :class="{ active : checkTypeActive(type) }"
            @click="setActiveType(type)"
          >
            {{ type.Title }}
          </button>
        </div>
        <div class="portal">
          <a class="portal__close" @click="setActiveType(null)">
            <img alt="Close" src="@/assets/images/icons/close-white.svg" width="29" height="29">
          </a>
          <div class="portal__container">
            <div class="portal__inner">
              <transition name="fade">
                <div class="job-type-wrapper" v-if="activeCharacter" :key="activeCharacter">
                    <div class="job-type" v-if="activeCharacter && activeCharacter.Title">
                      {{activeCharacter.Title}}
                    </div>
                </div>
              </transition>
              <img alt="Portal" src="@/assets/images/underground/portal.png" class="portal__image">
              <div
                v-for="(type, index) in screenContent.Classes"
                :key="`disk-type-${index}`"
                class="portal__disk"
                :class="[{ active : checkTypeActive(type) }, `portal__disk--${index}` ]"
                @click="setActiveType(type)"
              >
                <!-- icon white -->
                <picture v-if="type.IconWhite">
                  <img
                    class="portal__icon portal__icon--white"
                    :alt="`Icon white - ${index}`"
                    :src="type.IconWhite"
                    :srcset="`${type.IconWhite} 700w`"
                    sizes="100px"
                  >
                </picture>
                <!-- icon purple -->
                <picture v-if="type.IconPurple">
                  <img
                    class="portal__icon portal__icon--purple"
                    :alt="`Icon purple - ${index}`"
                    :src="type.IconPurple"
                    :srcset="`${type.IconPurple} 700w`"
                    sizes="100px"
                  >
                </picture>
              </div>
              <transition name="fade">
                <!-- active -->
                <div v-if="activeType" class="portal__active">
                  <img alt="Portal Window" src="@/assets/images/underground/portal-window.png" class="portal__image--window">
                  <button class="chevron chevron--left" @click="characterPrev">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 53 110">
                      <path class="chevron__line" d="M0,55L53,0l-5,22.28L15,55.48l33,31.83,5,22.69L0,55Z"/>
                    </svg>
                  </button>
                  <button class="chevron chevron--right" @click="characterNext">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 53 110">
                      <path class="chevron__line" d="M53,55,0,0,5,22.28l33,33.2L5,87.31,0,110Z"/>
                    </svg>
                  </button>
                </div>
                <!-- inactive -->
                <div v-else class="portal__inactive">
                  <img alt="Portal Unknown" src="@/assets/images/underground/portal-unknown.png" class="portal__image--unknown">
                </div>
              </transition>
              <transition name="fade">
                <div class="underlay z-1" v-if="activeCharacter" :key="activeCharacter">
                  <picture v-if="activeCharacter.Image">
                    <img
                      class="portal__character"
                      :alt="`${activeCharacter.Title}`"
                      :src="activeCharacter.Image"
                      :srcset="`${activeCharacter.Image} 700w`"
                      sizes="600px"
                    >
                  </picture>
                  <div 
                    v-if="activeCharacter && activeType.Characters.length >= 2" 
                    class="pagination-wrapper" 
                    :class="`l-${activeType.Characters.length}`"
                  >
                    <div 
                      v-for="(item, index) in activeType.Characters"
                      :key="index"
                      class="dot" 
                      :class="[{active : index === activeCharacterIndex}]" 
                      @click="paginationClick(index)"
                    />
                  </div>
                </div>
              </transition>
            </div>
            <transition name="fade" mode="out-in">
              <div :key="activeCharacter" class="portal__content" :class="{ 'portal__content--notset' : !activeCharacter }">
                <div v-if="activeCharacter">
                  <h4 v-if="screenContent.ClassText" class="mb-2">
                    {{ screenContent.ClassText }}
                  </h4>
                  <div class="portal__well">
                    <p v-if="activeCharacter.Title" class="lead bold">
                      {{ activeCharacter.Title }}
                    </p>
                    <div v-if="activeCharacter.Stat1Label" class="mb-1">
                      <div class="portal__stats portal__stats--labels flex-row">
                        <div class="col-4">
                          <small v-html="outputStat(activeCharacter.Stat1Label)" />
                        </div>
                        <div class="col-4">
                          <small v-html="outputStat(activeCharacter.Stat2Label)" />
                        </div>
                        <div class="col-4">
                          <small v-html="outputStat(activeCharacter.Stat3Label)" />
                        </div>
                      </div>
                      <div class="portal__stats flex-row m-0">
                        <div class="col-4">
                          <span v-html="outputStat(activeCharacter.Stat1Value)" />
                        </div>
                        <div class="col-4">
                          <span v-html="outputStat(activeCharacter.Stat2Value)" />
                        </div>
                        <div class="col-4">
                          <span v-html="outputStat(activeCharacter.Stat3Value)" />
                        </div>
                      </div>
                    </div>
                    <p v-if="activeCharacter.Description" class="portal__text">
                      {{ activeCharacter.Description }}
                    </p>
                  </div>
                </div>
                <div v-else>
                  <h4 v-if="screenContent.PromptTitle" class="mb-3">
                    {{ screenContent.PromptTitle }}
                  </h4>
                  <p v-if="screenContent.PromptText" class="portal__text">
                    {{ screenContent.PromptText }}
                  </p>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <!-- arrow -->
    <Arrow
      :text="screenContent.ButtonDown"
      direction="down"
      theme="yellow"
      @click="clickFooter"
    />
  </section>
</template>

<script>
import Arrow from '@/components/parts/Arrow.vue'
import UndergroundNpc1 from '@/components/npcs/UndergroundNpc1.vue'
import UndergroundNpc2 from '@/components/npcs/UndergroundNpc2.vue'
import UndergroundNpc3 from '@/components/npcs/UndergroundNpc3.vue'
import UndergroundNpc4 from '@/components/npcs/UndergroundNpc4.vue'

export default {
  name: 'Underground',

  components: {
    Arrow,
    UndergroundNpc1,
    UndergroundNpc2,
    UndergroundNpc3,
    UndergroundNpc4
  },

  data () {
    return {
      activeType: null,
      activeCharacterIndex: 0,
      activeCharacter: null
    }
  },

  computed: {
    screenContent () {
      return this.$store.getters.getContentUnderground
    },
    sceneName () {
      return this.$store.getters.getSceneName
    }
  },

  created() {
    if(this.screenContent && this.screenContent.Classes && this.screenContent.Classes[0]) { 
      this.setActiveType(this.screenContent.Classes[0]);
    }
  },

  mounted () {
    window.scrollTo(0, 0)
    this.$gtm.trackEvent({
      'event': 'View Landing Page Section',
      'lp_section': this.sceneName,
      'lp_loc': '2'
    })
  },

  methods: {
    outputStat (str) {
      str = str.replace(/(?:\r\n|\r|\n)/g, '<br>')
      return str.replace(' (', '<br>(')
    },
    setActiveType (type) {
      if (this.checkTypeActive(type) || type === null) {
        this.activeType = null
        this.activeCharacterIndex = 0
        this.activeCharacter = null
      } else {
        this.activeType = type
        this.activeCharacterIndex = 0
        this.setActiveCharacter()
        this.mobileScrollUp()
        this.$gtm.trackEvent({
          'event': 'Class Menu View',
          'lp_className': this.activeType.Title
        })
      }
    },
    mobileScrollUp () {
      const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth
      if (windowWidth < 576) {
        window.scrollTo(0, 0)
      }
    },
    checkTypeActive (type) {
      let isActive = false
      if (this.activeType && type) {
        if (this.activeType.Title === type.Title) {
          isActive = true
        }
      }
      return isActive
    },
    characterPrev () {
      this.activeCharacterIndex = this.activeCharacterIndex - 1
      this.setActiveCharacter()
      this.$gtm.trackEvent({
        'event': 'Class Navigation',
        'lp_className': this.activeCharacter.Title,
        'lp_NavDirection': 'Left'
      })
    },
    characterNext () {
      this.activeCharacterIndex = this.activeCharacterIndex + 1
      this.setActiveCharacter()
      this.$gtm.trackEvent({
        'event': 'Class Navigation',
        'lp_className': this.activeCharacter.Title,
        'lp_NavDirection': 'Right'
      })
    },
    setActiveCharacter () {
      if (this.activeType) {
        if (this.activeCharacterIndex < 0) {
          this.activeCharacterIndex = this.activeType.Characters.length - 1
        }
        if (this.activeCharacterIndex >= this.activeType.Characters.length) {
          this.activeCharacterIndex = 0
        }
        if (this.activeType.Characters.length > this.activeCharacterIndex) {
          this.activeCharacter = this.activeType.Characters[this.activeCharacterIndex]
        }
      }
    },
    clickFooter () {
      this.$store.dispatch('setScene', 'footer')
    },
    paginationClick(index) {
      if (index !== this.activeCharacterIndex) {
        this.activeCharacterIndex = index;
        this.setActiveCharacter();
        this.$gtm.trackEvent({
          'event': 'Class Navigation',
          'lp_className': this.activeCharacter.Title,
          'lp_NavDirection': 'Button'
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/scss/landing/variables.scss';
@import '@/assets/scss/landing/mixins.scss';

.scene--underground {
  padding: 0;

  .arrow--down {
    z-index: 6;

    @include breakpoint(lg) {
      bottom: 20px;
    }
  }
}
.content {
  &.typeset {
    z-index: 6;
  }
}
.container {
  min-height: 100vh;
  padding-top: 100px;
  padding-bottom: 280px;

  @include breakpoint(lg) {
    padding-bottom: 80px;
  }
  @media only screen and (max-height: 440px) and (orientation: landscape) {
    padding-top: 80px;
    padding-bottom: 260px;
  }
}
.portal {
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 80px 10px 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity .5s ease;

  @include breakpoint(sm) {
    position: relative;
    padding: 0;
    opacity: 1;
    pointer-events: auto;

    &:before {
      display: none;
    }
  }

  .typeset & {
    opacity: 1;
    pointer-events: auto;
  }

  &:before {
    content: '';
    position: absolute;
    z-index: 0;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: $black;
    opacity: .8;
  }
}
.portal__close {
  position: absolute;
  z-index: 1;
  top: 20px;
  left: 25px;
  display: block;
  cursor: pointer;
  opacity: 1;
  transition: opacity .5s ease;

  &:hover {
    opacity: .7;
  }

  @include breakpoint(sm) {
    display: none;
  }
}
.portal__container {
  position: relative;
  z-index: 1;

  @include breakpoint(lg) {
    display: flex;
    flex-direction: row;
    width: 100%;
    max-width: 1000px;
    margin: 0 auto;
  }
}
.portal__content {
  @include breakpoint(lg) {
    flex: 0 0 50%;
    max-width: 50%;

    @include breakpointHeight(970px) {
      margin: auto 0;
    }
  }

  &--notset {
    margin: auto 0;
  }
}
.portal__inner {
  position: relative;
  z-index: 0;
  width: 360px;
  max-width: 100%;
  display: inline-block;
  margin: 0 auto 35px;

  @include breakpoint(sm) {
    width: 450px;
    margin: 0 auto 70px;
  }
  @include breakpoint(lg) {
    margin: 0 auto;
  }
  @media only screen and (max-height: 440px) and (orientation: landscape) {
    width: 360px;
    margin-bottom: 40px;
    .chevron {
      height: 60px;
      width: 30px;
      top: calc(50% - 33px);
      &--right {
        right: 8px;
      }
      &--left {
        left: 7px;
      }
    }
  }
  @media only screen and (max-height: 400px) and (orientation: landscape) {
    width: 340px;
  }
  @media only screen and (max-height: 376px) and (orientation: landscape) {
    width: 320px;
  }
  @media only screen and (max-height: 320px) and (orientation: landscape) {
    width: 300px;
  }
}
.portal__image {
  width: 100%;

  &--unknown,
  &--window {
    position: absolute;
    z-index: 1;
    top: 50%;
    left: 50%;
    width: 70%;
    transform: translate3d(-50%, -50%, 0);
  }
}
.portal__character {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 50%;
  width: auto;
  height: auto;
  max-width: 70%;
  max-height: 75%;
  transform: translate3d(-50%, -54%, 0);
}
.portal__disk {
  position: absolute;
  z-index: 3;
  width: 70px;
  height: 70px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-image: url('../../assets/images/underground/portal-disk.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;

  @include breakpoint(sm) {
    width: 80px;
    height: 80px;
  }

  @media only screen and (max-height: 440px) and (orientation: landscape) {
    width: 65px;
    height: 65px;
  }

  &--0 {
    top: 7%;
    left: 7%;
  }
  &--1 {
    top: 7%;
    right: 7%;
  }
  &--2 {
    bottom: 7%;
    left: 7%;
  }
  &--3 {
    bottom: -6%;
    left: calc(50% - 35px);

    @include breakpoint(sm) {
      left: calc(50% - 40px);
    }
  }
  &--4 {
    bottom: 7%;
    right: 7%;
  }
}
.portal__icon {
  display: block;
  width: 30px;
  height: auto;
  transform-origin: center;
  transition: opacity .5s ease;

  @include breakpoint(sm) {
    width: 40px;
  }

  @media only screen and (max-height: 440px) and (orientation: landscape) {
    width: 30px;
  }

  &--white {
    opacity: 1;

    .portal__disk:hover &,
    .portal__disk.active & {
      opacity: 0;
    }
  }
  &--purple {
    position: absolute;
    z-index: 1;
    top: calc(50% - 15px);
    left: calc(50% - 15px);
    opacity: 0;

    @include breakpoint(sm) {
      top: calc(50% - 20px);
      left: calc(50% - 20px);
    }
    @media only screen and (max-height: 440px) and (orientation: landscape) {
      top: calc(50% - 15px);
      left: calc(50% - 15px);
    }

    .portal__disk:hover &,
    .portal__disk.active & {
      opacity: 1;
    }
  }
}
.portal__well {
  @include breakpoint(sm) {
    padding: 10px 5px 1px;
    border-radius: 4px;
    background-color: rgba(0, 0, 0, .3);
  }
}
.portal__stats {
  max-width: 700px;
  margin: 0 auto;

  &--labels {
    align-items: flex-start;
    margin-bottom: 5px;
  }

  .col-4 {
    padding: 0 10px 5px;
  }
  small {
    display: block;
    font-size: 1.2rem;
    line-height: 2rem;
  }
  span {
    display: block;
    font-size: 1.8rem;
    line-height: 2.2rem;
    font-family: $font-bold;
  }
}
.platform {
  display: none;
  position: absolute;
  z-index: 2;
  top: 500px;
  height: 86px;
  width: auto;

  @include breakpoint(lg) {
    display: block;
  }

  &--left {
    left: 0;
  }
  &--right {
    right: 0;
  }
}
.job-type-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 4;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 0;
  pointer-events: none;
  .job-type {
    background: $yellow;
    color: $dark;
    position: relative;
    width: auto;
    white-space: nowrap;
    font-family: $font-bold;
    text-transform: uppercase;
    border-radius: 5px;
    font-size: 1.4rem;
    padding: 10px;
    border: 1px solid $brown;
    z-index: 2;
    @media (min-width: 576px) and (min-height: 440px) {
      font-size: 2rem;
      margin-top: 10px;
      padding: 12px 15px;
    }
    @media only screen and (max-height: 440px) and (orientation: landscape) {
      font-size: 1.4rem;
      padding: 10px;
    }
    &:after {
      content: '';
      position: absolute;
      z-index: 2;
      width: 15px;
      height: 15px;
      top: auto;
      bottom: -8.2px;
      left: 50%;
      background-color: $yellow;
      border-right: 1px solid $brown;
      border-bottom: 1px solid $brown;
      transform: translateX(-50%) rotate(45deg);
    }
  }
}
.chevron {
  height: 60px;
  width: 30px;
  top: calc(50% - 33px);
  &--right {
    right: 8px;
  }
  &--left {
    left: 7px;
  }
  @include breakpoint(sm) {
    height: 100px;
    width: 48px;
    top: calc(50% - 53px);
    &--right {
      right: 4px;
    }
    &--left {
      left: 3px;
    }
  }
  .chevron__line {
    stroke: $lime;
    fill: $lime;
  }
  &:hover {
    .chevron__line {
      stroke: $lemon;
      fill: $lemon;
    }
  }
}
.pagination-wrapper {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 18%;
  display: flex;
  flex-wrap: nowrap;
  z-index: 5;
  @include breakpoint(sm) {
    bottom: 17%;
  }
  .dot {
    width: 17px;
    height: 17px;
    background-color: #757f02;
    border-radius: 50%;
    margin-left: 2px;
    margin-right: 2px;
    border: 1px solid $brown;
    transition: background-color .2s;
    &.active {
      background-color: $yellow;
      &:hover {
        cursor: default;
        transition: background-color .2s;
      }
    }
    &:hover {
      background-color: $yellow;
      cursor: pointer;
    }
    @include breakpoint(sm) {
      width: 20px;
      height: 20px;
      margin-left: 4px;
      margin-right: 4px;
      border: 2px solid $brown;
    }
    @media only screen and (max-height: 440px) and (orientation: landscape) {
      width: 18px;
      height: 18px;
      margin-left: 3px;
      margin-right: 3px;
      border: 2px solid $brown;
    }
  }
  &.l-3 {
    .dot {
      &:nth-child(2) {
        margin-top: 2px;
      }
    }
  }
  &.l-4 {
    .dot {
      &:nth-child(3n + 1) {
        margin-top: -4px;
      }
    }
  }
  &.l-5 {
    .dot {
      &:nth-child(2n) {
        margin-top: -2px;
      }
      &:nth-child(4n+1) {
        margin-top: -8px;
      }
    }
  }
  &.l-6 {
    .dot {
      &:nth-child(5n + 1) {
        margin-top: -14px;
      }
      &:nth-child(2), &:nth-child(5) {
        margin-top: -4px;
      }
    }
  }
  &.l-7 {
    .dot {
      &:nth-child(3), &:nth-child(5) {
        margin-top: -2px;
      }
      &:nth-child(2), &:nth-child(6) {
        margin-top: -10px;
      }
      &:first-child, &:last-child {
        margin-top: -26px;
      }
    }
  }
}
</style>
